/**
 * kitMethodLabels — canonical wording for `booking.kit_method`
 * =============================================================
 *
 * Feb 2026 — single source of truth for how `kit_method` renders on
 * every UI surface. Prevents each screen from inventing its own
 * wording and stops raw enum strings ("existing_bottle") from leaking
 * into customer / partner / admin surfaces.
 *
 * Three audiences → three label maps. Each surface picks the map that
 * matches its audience (see per-surface JSDoc below).
 *
 * The enum values themselves live server-side in
 * `backend/kit_service.CUSTOMER_KIT_METHODS + ADMIN_ONLY_KIT_METHODS`.
 * Keep these two in sync manually — there's no runtime broadcast.
 */

/** Customer-facing wording — shown on retail booking pages and
 *  confirmation emails to end-customers. Friendly & instructional. */
export const CUSTOMER_KIT_LABELS = {
  mail_out:             "Sample kit will be posted",
  local_pickup_dropoff: "Pick up bottle from Noosaville",
  existing_bottle:      "Bringing own clean 500 mL bottle",
  admin_supplied:       "Sample kit supplied by Aware",
};

/** Partner-facing wording — shown on the partner portal's read-only
 *  booking views. Frames the choice around the customer, not the
 *  partner, since partners do not create bookings today. */
export const PARTNER_KIT_LABELS = {
  mail_out:             "Lab sample kit will be sent to the customer",
  local_pickup_dropoff: "Customer will drop sample to Noosaville",
  existing_bottle:      "Customer already has a sample kit or bottle",
  admin_supplied:       "Sample kit supplied by admin/lab",
};

/** Admin / staff wording — shown on admin Booking Detail, KitsToday,
 *  Lab Tech surfaces. Terse operational phrasing. */
export const ADMIN_KIT_LABELS = {
  mail_out:             "Mail-out — lab kit posted to customer",
  local_pickup_dropoff: "Customer pickup / drop-off at Noosaville",
  existing_bottle:      "Customer has kit or own bottle — no dispatch",
  admin_supplied:       "Kit supplied by admin/lab at counter",
  technician_collected: "Technician collects sample during service",
};

/** Safe getter — returns the label for `key` from the given map, or
 *  a friendly fallback that avoids raw enum leakage. */
export function labelFor(map, key) {
  if (!key) return "—";
  return map[key] || String(key).replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
